import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';

// Import web pages
import Menu from './pages/Menu';                                                   // The client-facing menu page
import Orders from './pages/Orders';                                               // The full notification message page

// Import data contexts
import PartnerFullMenuDataContext, { AdminMenuRefreshContext, MenuDataType } from './contexts/MenuData';    // The food maker's menu data context
import PartnerDataContext, { PartnerDataRefreshContext, PartnerDataType } from './contexts/PartnerData';      // The partner's data context
import UserDataContext, { UserDataType } from './contexts/UserData';               // The user data context
import SessionDataContext, { SessionDataType, useSessionData } from './contexts/SessionData';      // The order data context
import AuthContext, { AuthContextType, AuthProfile, AuthStatus, useAuthData } from './contexts/AuthData';
import { AuthFlowStart, AuthPurpose, IdentitySettings, authReturnUrl, identityRequest } from './authIdentityApi';
import AdminAccessContext, { useAdminAccess as useAdminAccessContext, AdminAccessContextType, AdminAccessResponse, AdminAccessStatus, parseAdminAccessResponse } from './contexts/AdminAccessData';
import { ADMIN_AUTH_FAILURE_EVENT, adminFetch } from './adminApi';
import AdminDestinationChooser from './components/AdminDestinationChooser';
import ProfileAuthModal, { DEFAULT_PROFILE_LABELS } from './components/ProfileAuthModal';
import HomeDataContext, { HomeDataContextType, HomePlace, HomePlacesRequest, HomePlacesStatus } from './contexts/HomeData';
import AppVersionInfoModal from './components/AppVersionInfoModal';
import BrowserTabBranding from './components/BrowserTabBranding';
import { isWholesaleBottleShopHost, isWholesaleBottleShopOrigin, resolveMenuRequestUserIdForHost } from './wholesaleBottleShop';
import {
    buildProdBackendOverrideUrl,
    canSwitchTestOriginToProdBackend,
    isLocalFrontendHost,
    isTestFrontendOrigin,
    MAIN_FRONTEND_ORIGINS,
    removeBackendOverrideFromUrl,
    resolveBackendUrlForOrigin,
    TEST_FRONTEND_ORIGINS,
    urlBackendTest,
    urlLocalTestBackend,
} from './backendSelection';
import {
    readSessionDataStorage,
    readUserDataStorage,
} from './storageRecovery';
import { createSessionBootstrapCoordinator } from './sessionBootstrap';
import {
    FoodMakersStatusDataState,
    normalizeFoodMakersPayload,
} from './foodMakersStatus';

export { urlBackendTest, urlLocalTestBackend };

let menuEditingModulePromise: Promise<typeof import('./pages/MenuEditing')> | null = null;
function loadMenuEditingModule(): Promise<typeof import('./pages/MenuEditing')> {
    menuEditingModulePromise ??= import('./pages/MenuEditing');
    return menuEditingModulePromise;
}
const MenuEditing = lazy(loadMenuEditingModule);
const ProductEditing = lazy(() => import('./pages/ProductEditing'));
const Home = lazy(() => import('./pages/Home'));
const Search = lazy(() => import('./pages/Search'));
const FoodMakersStatus = lazy(() => import('./pages/FoodMakersStatus'));
const TelegramLinkConverter = lazy(() => import('./pages/TelegramLinkConverter'));

const currentOrigin: string = window.location.origin;
const currentHostname: string = window.location.hostname.toLowerCase();
const currentSearch: string = window.location.search;
const DEFAULT_HOME_LOCATION = 'Montenegro_Podgorica';
const HOME_PLACES_PATHS = new Set(['/Home', '/Search']);
const ADMIN_PATHS = new Set(['/MenuEditing', '/ProductEditing', '/FoodMakersStatus', '/TelegramLinkConverter']);

type FrontendBootstrapResponse = {
    matched: boolean;
    food_maker?: string;
    canonical_path?: string;
};

type SessionBootstrapResult = {
    sessionData: SessionDataType;
    userData: UserDataType;
};

/**
 * AppRoutePathObserver
 *
 * Reports React Router pathname changes back to App so App-owned data effects
 * can remain route-scoped during client-side navigation. It receives a stable
 * pathname callback, depends only on Router location context, and performs no
 * navigation or data loading itself.
 */
function AppRoutePathObserver({ onPathnameChange }: { onPathnameChange: (pathname: string) => void }) {
    const { pathname } = useLocation();

    useEffect(() => {
        onPathnameChange(pathname);
    }, [onPathnameChange, pathname]);

    return null;
}

// React Strict Mode remounts App during development. Sharing only the pending
// load prevents a duplicate bootstrap without retaining stale data forever.
const sessionBootstrapCoordinator = createSessionBootstrapCoordinator<SessionBootstrapResult | null>();

function resolveHardcodedFrontendBootstrap(origin: string): FrontendBootstrapResponse | null {
    // !!! TEMPORARY HARDCODED WORKAROUND: wholesale Bottle Shop must bootstrap
    // !!! exactly like bottle-shop.me even if backend frontend_bootstrap is
    // !!! missing or misconfigured. Keep the current host and force /Menu.
    if (isWholesaleBottleShopOrigin(origin)) {
        return {
            matched: true,
            food_maker: 'cideria',
            canonical_path: '/Menu',
        };
    }

    return null;
}

/**
 * First-party i-food domains and test/dev origins should keep the legacy
 * frontend-only bootstrap flow. Custom domains are resolved at runtime by
 * backend configuration so new personal domains can be added without rebuilds.
 */
function shouldSkipDomainBootstrap(origin: string, hostname: string): boolean {
    return isLocalFrontendHost(hostname)
        || MAIN_FRONTEND_ORIGINS.includes(origin)
        || TEST_FRONTEND_ORIGINS.includes(origin);
}

export function canSwitchTestDomainBackendToProd(): boolean {
    return canSwitchTestOriginToProdBackend(window.location.origin, backendUrl);
}

export function switchTestDomainBackendToProd(): void {
    if (!isTestFrontendOrigin(window.location.origin)) {
        return;
    }

    window.location.href = buildProdBackendOverrideUrl(window.location.href);
}

function normalizeFoodMakerCode(foodMaker: string | null): string | null {
    if (foodMaker === 'sushi-market') {
        return 'sushi_market';
    }

    if (foodMaker === 'i-Pizza') {
        return 'i_Pizza';
    }

    return foodMaker;
}

function applyDefaultLanguageToOrderData(orderData: SessionDataType): void {
    if ('lang' in orderData && orderData.lang != null && orderData.lang !== 'undefined') {
        return;
    }

    if (orderData.channel === 'telegram') {
        orderData.lang = 'ru';
    } else if (orderData.channel === 'viber') {
        orderData.lang = 'cn';
    } else {
        orderData.lang = 'en';
    }
}

function buildTelegramContacts(
    currentContacts: SessionDataType['contacts'],
    username: string | null,
): SessionDataType['contacts'] {
    const nextContacts = { ...(currentContacts ?? {}) };

    return {
        ...nextContacts,
        preferableMethod: 'telegram',
        telegram: {
            ...(nextContacts.telegram ?? {}),
            userName: username ?? undefined,
        },
    };
}

function normalizeTelegramChannelWithoutUserId(orderData: SessionDataType): void {
    if (orderData.channel !== 'telegram') {
        return;
    }

    const telegramUserId = resolvePreferredTelegramUserId(orderData.telegram_user_id, orderData.user_id);
    if (telegramUserId) {
        orderData.user_id = telegramUserId;
        orderData.telegram_user_id = telegramUserId;
        return;
    }

    orderData.channel = 'web';
    if (orderData.contacts?.preferableMethod === 'telegram') {
        orderData.contacts = {
            ...orderData.contacts,
            preferableMethod: 'phone',
        };
    }
}

/**
 * Telegram Mini App orders must use the external Telegram user id, while the
 * authenticated backend profile may expose a different internal user id. Keep
 * the Telegram-specific value as the preferred identity whenever it exists.
 */
function resolvePreferredTelegramUserId(
    telegramUserId: string | null | undefined,
    currentUserId?: string | null,
    fallbackProfileUserId?: string | null,
): string | null {
    if (telegramUserId != null && telegramUserId.trim().length > 0) {
        return telegramUserId;
    }

    if (currentUserId != null && currentUserId.trim().length > 0) {
        return currentUserId;
    }

    if (fallbackProfileUserId != null && fallbackProfileUserId.trim().length > 0) {
        return fallbackProfileUserId;
    }

    return null;
}

function applyTelegramIdentityToSessionData(
    currentData: SessionDataType | null,
    identity: TelegramMiniAppIdentity,
): SessionDataType {
    const telegramUserId = resolvePreferredTelegramUserId(
        identity.userId,
        currentData?.telegram_user_id ?? currentData?.user_id ?? null,
    );
    const nextData: SessionDataType = {
        ...(currentData ?? {}),
        user_id: telegramUserId,
        telegram_user_id: telegramUserId,
        channel: 'telegram',
        channel_first_name: identity.firstName ?? null,
        channel_last_name: identity.lastName ?? null,
        channel_user_name: identity.username ?? null,
        contacts: buildTelegramContacts(currentData?.contacts, identity.username),
        update_time: Date.now(),
    };

    applyDefaultLanguageToOrderData(nextData);
    return nextData;
}

function normalizeCanonicalTarget(pathOrUrl: string): URL {
    return new URL(pathOrUrl, window.location.origin);
}

function buildCanonicalMenuUrl(pathname: string): string {
    const correctedUrl = new URL(window.location.href);
    const canonicalTarget = normalizeCanonicalTarget(pathname);
    correctedUrl.pathname = canonicalTarget.pathname;

    if (canonicalTarget.search) {
        correctedUrl.search = canonicalTarget.search;
    }

    correctedUrl.searchParams.delete('fm');
    return correctedUrl.toString();
}

function buildPublicMenuUrl(foodMakerCode: string, lang: string, userId: string | null): string {
    const menuUrl = new URL(`${backendUrl}/menu`);
    menuUrl.searchParams.set('lang', lang);
    menuUrl.searchParams.set('food_maker', foodMakerCode);

    // !!! TEMPORARY WORKAROUND: wholesale Bottle Shop must request /menu with a
    // !!! special user_id to get opt-client pricing. Do not remove without
    // !!! backend/business confirmation.
    const requestUserId = resolveMenuRequestUserIdForHost(window.location.hostname, userId);
    if (requestUserId) {
        menuUrl.searchParams.set('user_id', requestUserId);
    }

    return menuUrl.toString();
}

/**
 * Custom domains resolve their partner binding from backend runtime
 * configuration. A failed lookup must not block startup because legacy `fm`
 * query-driven access still needs to work as a fallback.
 */
async function resolveFrontendBootstrap(): Promise<FrontendBootstrapResponse | null> {
    const hardcodedBootstrap = resolveHardcodedFrontendBootstrap(currentOrigin);
    if (hardcodedBootstrap) {
        return hardcodedBootstrap;
    }

    if (shouldSkipDomainBootstrap(currentOrigin, currentHostname)) {
        return null;
    }

    try {
        const response = await fetch(
            `${backendUrl}/frontend_bootstrap?host=${encodeURIComponent(currentHostname)}`,
            {
                method: 'GET',
                headers: {'ngrok-skip-browser-warning': 'true'},
            }
        );

        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json() as FrontendBootstrapResponse;

        return {
            matched: Boolean(payload?.matched),
            food_maker: payload?.food_maker,
            canonical_path: payload?.canonical_path,
        };
    } catch (error) {
        console.warn('Failed to resolve the frontend bootstrap for the current host.', error);
        return null;
    }
}

console.log('currentOrigin', currentOrigin);
export let backendUrl: string = resolveBackendUrlForOrigin(currentOrigin, currentHostname, currentSearch);

/**
 * A spinner click may select production for this one page load. Clear its URL
 * marker immediately so refreshing a test-domain page restores the test backend.
 */
if (isTestFrontendOrigin(currentOrigin) && backendUrl !== urlBackendTest) {
    window.history.replaceState(
        window.history.state,
        '',
        removeBackendOverrideFromUrl(window.location.href),
    );
}

// The global parameters
const fileLocationUrl = 'https://api.i-food.me/files/'
export const productImagesPath = fileLocationUrl + 'assets/img/ImagesPages/products/'      // The path where we keep all product images
export const logoImagesPath = fileLocationUrl + 'assets/img/icons/'                        // The path where we keep all logos


// Getting the telegram initializing data
type TelegramMiniAppUser = {
    id?: string | number;
    first_name?: string;
    last_name?: string;
    username?: string;
};

type TelegramWebApp = {
    initData?: string;
    initDataUnsafe?: {
        user?: TelegramMiniAppUser;
    };
};

interface TelegramWindow extends Window {
  Telegram?: {
    WebApp?: TelegramWebApp;
  };
}

type TelegramMiniAppIdentity = {
    initData: string | null;
    userId: string | null;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
};

type TelegramIdentityRequirement = 'initData' | 'userId' | 'either';

const TELEGRAM_IDENTITY_WAIT_TIMEOUT_MS = 1200;
const TELEGRAM_IDENTITY_POLL_INTERVAL_MS = 100;

/**
 * Telegram WebApp data may appear after the module was first evaluated.
 * Read it at runtime instead of caching a stale module-level reference.
 */
function getTelegramWebApp(): TelegramWebApp | undefined {
    return (window as unknown as TelegramWindow).Telegram?.WebApp;
}

function getTelegramMiniAppIdentity(): TelegramMiniAppIdentity | null {
    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp) {
        return null;
    }

    const telegramUser = telegramWebApp.initDataUnsafe?.user;
    const initData = typeof telegramWebApp.initData === 'string' && telegramWebApp.initData.trim().length > 0
        ? telegramWebApp.initData
        : null;

    return {
        initData,
        userId: telegramUser?.id !== undefined && telegramUser?.id !== null ? String(telegramUser.id) : null,
        firstName: telegramUser?.first_name ?? null,
        lastName: telegramUser?.last_name ?? null,
        username: telegramUser?.username ?? null,
    };
}

function hasTelegramMiniAppIdentity(
    identity: TelegramMiniAppIdentity | null,
    requirement: TelegramIdentityRequirement
): boolean {
    if (!identity) {
        return false;
    }

    if (requirement === 'initData') {
        return Boolean(identity.initData);
    }

    if (requirement === 'userId') {
        return Boolean(identity.userId);
    }

    return Boolean(identity.initData || identity.userId);
}

async function waitForTelegramMiniAppIdentity(
    requirement: TelegramIdentityRequirement,
    timeoutMs = TELEGRAM_IDENTITY_WAIT_TIMEOUT_MS,
    pollIntervalMs = TELEGRAM_IDENTITY_POLL_INTERVAL_MS,
): Promise<TelegramMiniAppIdentity | null> {
    const immediateIdentity = getTelegramMiniAppIdentity();
    if (hasTelegramMiniAppIdentity(immediateIdentity, requirement)) {
        return immediateIdentity;
    }

    const deadline = Date.now() + timeoutMs;

    return new Promise((resolve) => {
        const pollIdentity = () => {
            const nextIdentity = getTelegramMiniAppIdentity();
            if (hasTelegramMiniAppIdentity(nextIdentity, requirement) || Date.now() >= deadline) {
                resolve(nextIdentity);
                return;
            }

            window.setTimeout(pollIdentity, pollIntervalMs);
        };

        pollIdentity();
    });
}

export type NotificationMessageLanguage = 'ru' | 'en' | 'cn' | 'tr' | 'ua';

export interface NotificationMessageRequest {
    msgId: string;
    language: NotificationMessageLanguage;
}

export interface NotificationMessageResponse {
    messageText: string;
}

/**
 * fetchNotificationMessage
 *
 * App-level API helper for loading the complete Telegram notification text.
 * Receives the message id and fallback language parsed by the orders page.
 * Depends on the shared backendUrl selection above and returns plain text from
 * the backend without adding page-specific rendering decisions.
 */
export async function fetchNotificationMessage(request: NotificationMessageRequest): Promise<NotificationMessageResponse> {
    const apiUrl = `${backendUrl}/notification_message?msg_id=${encodeURIComponent(request.msgId)}&ln=${encodeURIComponent(request.language)}`;

    const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {'ngrok-skip-browser-warning': 'true'},
    });

    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
    }

    return {messageText: await response.text()};
}

async function requestFullMenuData(foodMakerCode: string): Promise<MenuDataType> {
    const response = await adminFetch(`/get_full_menu?food_maker=${encodeURIComponent(foodMakerCode)}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch the full menu data: ${response.status}`);
    }

    return response.json() as Promise<MenuDataType>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isHomePlace(value: unknown): value is HomePlace {
    if (!isRecord(value)) {
        return false;
    }

    return (
        typeof value.name_code === 'string'
        && typeof value.food_maker_key === 'string'
        && typeof value.location === 'string'
    );
}

function getHomePlacesFromPayload(payload: unknown): HomePlace[] {
    if (Array.isArray(payload)) {
        return payload.filter(isHomePlace);
    }

    if (isRecord(payload) && Array.isArray(payload.food_makers)) {
        return payload.food_makers.filter(isHomePlace);
    }

    return [];
}

async function requestHomePlaces(request: HomePlacesRequest): Promise<HomePlace[]> {
    const apiUrl = new URL(`${backendUrl}/food_makers`);
    apiUrl.searchParams.set('lang', request.language);
    apiUrl.searchParams.set('location', request.location);
    apiUrl.searchParams.set('loc', request.location);

    if (request.userId) {
        apiUrl.searchParams.set('user_id', request.userId);
        apiUrl.searchParams.set('userID', request.userId);
    }

    const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: {'ngrok-skip-browser-warning': 'true'},
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch the home page places: ${response.status}`);
    }

    const places = getHomePlacesFromPayload(await response.json() as unknown);
    console.log('[Home] foodmakers data', {
        request,
        url: apiUrl.toString(),
        count: places.length,
        places,
    });

    return places;
}

/**
 * Loads the complete food-maker collection for the global status page. Unlike
 * the customer Home request, this deliberately sends no location or user-id
 * filters so the backend can return its full language-specific collection.
 */
async function requestFoodMakersStatus(language: string) {
    const response = await adminFetch(`/admin/food_makers?lang=${encodeURIComponent(language)}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch food makers: ${response.status}`);
    }

    return normalizeFoodMakersPayload(await response.json() as unknown);
}

/**
 * RouteLoadingState
 *
 * Shared full-page loading surface for lazy route chunks and admin-only data
 * fetches. Keeps the public menu bootstrap lightweight while still giving
 * editing routes an explicit loading state instead of rendering partial forms.
 */
function RouteLoadingState({ message }: { message: string }) {
    const canSwitchBackend = canSwitchTestDomainBackendToProd();

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light px-4">
            <div className="text-center">
                {canSwitchBackend ? (
                    <button
                        type="button"
                        className="btn p-0 border-0 bg-transparent mb-3"
                        aria-label="Switch to production backend"
                        title="Switch to production backend"
                        onClick={switchTestDomainBackendToProd}
                    >
                        <Spinner animation="border" variant="success" />
                    </button>
                ) : (
                    <Spinner animation="border" variant="success" className="mb-3" />
                )}
                <div className="text-secondary fw-semibold">{message}</div>
            </div>
        </div>
    );
}

/**
 * TestBackendNotificationBar
 *
 * Displays a compact app-wide backend environment indicator above every React
 * route. It receives no props and depends on the shared `backendUrl` selected
 * during app bootstrap. The component has no side effects; it only renders when
 * the frontend is connected to a non-production backend so test/local sessions
 * are visible on public and admin pages.
 */
function TestBackendNotificationBar() {
    const notificationText =
        backendUrl === urlBackendTest
            ? 'The test backend server is using'
            : backendUrl === urlLocalTestBackend
                ? 'The local backend server is using'
                : '';

    if (!notificationText) {
        return null;
    }

    return (
        <div className="bg-success text-white text-center small fw-semibold py-1">
            {notificationText}
        </div>
    );
}

/**
 * TemporaryStaticHomeRedirect
 *
 * Sends visitors from the React root route to the legacy static i-food page
 * served from `public/The_app`. It receives no props, depends only on the
 * current browser location, and performs a one-time navigation side effect.
 * Wholesale Bottle Shop is a menu-only domain, so it keeps the menu bootstrap
 * instead of the temporary public home page.
 */
function TemporaryStaticHomeRedirect() {
    useEffect(() => {
        if (isWholesaleBottleShopHost(window.location.hostname)) {
            window.location.replace(buildCanonicalMenuUrl('/Menu'));
            return;
        }

        window.location.replace(`/The_app/index.html${window.location.search}${window.location.hash}`);
    }, []);

    return <RouteLoadingState message="Loading home page..." />;
}

/**
 * DevelopmentRenderErrorTrigger
 *
 * Throws only in non-production builds when the e2e-only query flag is present.
 * It receives no props, depends only on the current browser URL and build mode,
 * and intentionally has no side effects. This gives Playwright a deterministic
 * way to verify that the app-level recovery boundary replaces white screens.
 */
function DevelopmentRenderErrorTrigger() {
    const shouldForceRecoveryError = process.env.NODE_ENV !== 'production'
        && new URLSearchParams(window.location.search).get('__force_app_recovery_error') === '1';

    if (shouldForceRecoveryError) {
        throw new Error('Forced app recovery boundary test error.');
    }

    return null;
}

/**
 * AdminAccessProvider
 *
 * Owns the effective restaurant and global permissions for the authenticated
 * profile supplied by App. It fetches session-bound grants, rejects stale
 * responses after identity changes, and clears protected App data on recheck.
 */
function AdminAccessProvider({ authStatus, authUser, refreshAuthSession, clearAdminCaches, children }: {
    authStatus: AuthStatus;
    authUser: AuthProfile | null;
    refreshAuthSession: () => Promise<void>;
    clearAdminCaches: () => void;
    children: ReactNode;
}) {
    const [status, setStatus] = useState<AdminAccessStatus>('idle');
    const [access, setAccess] = useState<AdminAccessResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);
    const pending = useRef<Promise<void> | null>(null);
    const identity = authStatus === 'authenticated'
        ? String(authUser?.user_id ?? authUser?.email ?? authUser?.channel_user_name ?? '')
        : '';

    const refresh = useCallback((): Promise<void> => {
        if (authStatus !== 'authenticated' || !authUser) return Promise.resolve();
        if (pending.current) return pending.current;
        const requestGeneration = generation.current;
        setStatus('loading');
        clearAdminCaches();
        setAccess(null);
        setError(null);
        const request = (async () => {
            try {
                const response = await fetch(`${backendUrl}/auth/admin-access`, {
                    credentials: 'include',
                    cache: 'no-store',
                    headers: { 'ngrok-skip-browser-warning': 'true' },
                });
                if (response.status === 401) {
                    await refreshAuthSession();
                    throw new Error('Please sign in again.');
                }
                if (!response.ok) throw new Error(`Permission check failed: ${response.status}`);
                const parsedAccess = parseAdminAccessResponse(await response.json() as unknown);
                if (generation.current === requestGeneration) {
                    setAccess(parsedAccess);
                    setStatus('ready');
                }
            } catch (reason) {
                if (generation.current === requestGeneration) {
                    setAccess(null);
                    setStatus('error');
                    setError(reason instanceof Error ? reason.message : 'Unable to check admin access.');
                }
            }
        })().finally(() => { if (pending.current === request) pending.current = null; });
        pending.current = request;
        return request;
    }, [authStatus, authUser, refreshAuthSession, clearAdminCaches]);

    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    useEffect(() => {
        generation.current += 1;
        pending.current = null;
        setAccess(null);
        clearAdminCaches();
        setError(null);
        setStatus('idle');
        if (authStatus === 'authenticated') void refreshRef.current();
    }, [identity, authStatus, clearAdminCaches]); // Identity changes invalidate all earlier permission responses.

    useEffect(() => {
        const onFailure = (event: Event) => {
            const statusCode = (event as CustomEvent<number>).detail;
            generation.current += 1;
            pending.current = null;
            setAccess(null);
            clearAdminCaches();
            setError(statusCode === 401 ? 'Please sign in again.' : 'Admin access was denied.');
            setStatus('error');
            if (statusCode === 401) void refreshAuthSession();
        };
        window.addEventListener(ADMIN_AUTH_FAILURE_EVENT, onFailure);
        return () => window.removeEventListener(ADMIN_AUTH_FAILURE_EVENT, onFailure);
    }, [refreshAuthSession, clearAdminCaches]);

    const contextValue: AdminAccessContextType = {
        status: authStatus === 'authenticated' ? status : 'idle',
        access: authStatus === 'authenticated' ? access : null,
        error,
        refresh,
        canManage: (foodMaker) => authStatus === 'authenticated'
            && status === 'ready'
            && !!access?.restaurants.some((restaurant) => restaurant.food_maker_key === foodMaker),
    };
    return <AdminAccessContext.Provider value={contextValue}>{children}</AdminAccessContext.Provider>;
}

/**
 * AdminRouteGuard
 *
 * Receives an admin page and an optional global permission requirement. It
 * reads auth, grants, route identity, and selected restaurant from shared App
 * state; it rechecks access before mounting protected data or editor effects.
 */
function AdminRouteGuard({ children, global = false, onSelectRestaurant }: { children: ReactNode; global?: boolean; onSelectRestaurant?: (foodMaker: string) => void }) {
    const auth = useAuthData();
    const admin = useAdminAccessContext();
    const location = useLocation();
    const navigate = useNavigate();
    const session = useSessionData();
    const [showSignIn, setShowSignIn] = useState(false);
    const foodMaker = new URLSearchParams(location.search).get('fm')?.trim() ?? '';
    const checkedRoute = useRef<string>('');
    useEffect(() => {
        if (auth?.authStatus !== 'authenticated' || !admin) return;
        const routeKey = `${location.pathname}${location.search}`;
        if (admin.status === 'idle' || (admin.status === 'ready' && checkedRoute.current !== routeKey)) {
            checkedRoute.current = routeKey;
            void admin.refresh();
        }
    }, [auth?.authStatus, admin, location.pathname, location.search]);
    if (!auth || auth.authStatus === 'loading') return <RouteLoadingState message="Checking sign-in..." />;
    if (auth.authStatus !== 'authenticated') return <>
        <div className="container py-5 text-center"><Alert variant="warning">Sign in to open the admin console.</Alert>
            <Button variant="success" onClick={() => setShowSignIn(true)}>Sign in</Button></div>
        <ProfileAuthModal show={showSignIn} onHide={() => setShowSignIn(false)} labels={DEFAULT_PROFILE_LABELS} />
    </>;
    if (!admin || admin.status === 'idle' || admin.status === 'loading') return <RouteLoadingState message="Checking admin access..." />;
    if (admin.status === 'error') return <div className="container py-5 text-center">
        <Alert variant="warning">{admin.error ?? 'Unable to check admin access.'}</Alert>
        <div className="d-flex gap-2 justify-content-center">
            <Button variant="success" onClick={() => void admin.refresh()}>Retry</Button>
            {admin.error === 'Please sign in again.' &&
                <Button variant="outline-success" onClick={() => setShowSignIn(true)}>Sign in</Button>}
        </div>
        <ProfileAuthModal show={showSignIn} onHide={() => setShowSignIn(false)} labels={DEFAULT_PROFILE_LABELS} />
    </div>;
    if (global) {
        return admin.access?.can_view_food_makers_status ? <>{children}</>
            : <div className="container py-5"><Alert variant="danger">Access denied.</Alert></div>;
    }
    if (!foodMaker && !admin.access?.restaurants.length) return <div className="container py-5"><Alert variant="danger">Access denied.</Alert></div>;
    if (!foodMaker) return <div className="container py-5"><h1 className="h4">Choose a restaurant</h1>
        <AdminDestinationChooser onSelect={(path) => navigate(path)} restaurantsOnly /></div>;
    if (!admin.canManage(foodMaker)) return <div className="container py-5"><Alert variant="danger">Access denied.</Alert></div>;
    if (session?.food_maker !== foodMaker) return <RestaurantSessionSync foodMaker={foodMaker} onSelectRestaurant={onSelectRestaurant} />;
    return <>{children}</>;
}

/** Synchronizes the authorized URL restaurant with App-owned session metadata before mounting editors. */
function RestaurantSessionSync({ foodMaker, onSelectRestaurant }: {
    foodMaker: string;
    onSelectRestaurant?: (foodMaker: string) => void;
}) {
    useEffect(() => { onSelectRestaurant?.(foodMaker); }, [foodMaker, onSelectRestaurant]);
    return <RouteLoadingState message="Loading selected restaurant..." />;
}

/**
 * AdminFullMenuBoundary
 *
 * Loads `/get_full_menu` only for admin-facing routes that still depend on the
 * editable menu payload and its translation map. It reads the current
 * `food_maker` from shared session state, owns the fetch lifecycle locally, and
 * provides the resolved value through `PartnerFullMenuDataContext`.
 *
 * Data:
 * - Receives child route content as `children`
 * - Depends on `SessionDataContext` for the current `food_maker`
 * - Provides `MenuDataType | null` to editing pages
 *
 * Side effects:
 * - Requests `/get_full_menu` on mount and when `food_maker` changes
 * - Renders a retryable error state instead of exposing partial admin UI
 */
function AdminFullMenuBoundary({ children }: { children: ReactNode }) {
    const sessionData = useSessionData();
    const foodMakerCode = sessionData?.food_maker ?? '';
    const lastRequestedFoodMakerRef = useRef<string>('');
    const [fullMenuData, setFullMenuData] = useState<MenuDataType | null>(null);
    const [loadedFoodMaker, setLoadedFoodMaker] = useState<string>('');
    const requestSerial = useRef(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadFullMenuData = useCallback(async () => {
        if (!foodMakerCode) {
            setFullMenuData(null);
            setErrorMessage('No food maker selected for the admin page.');
            setIsLoading(false);
            return;
        }

        const serial = ++requestSerial.current;
        setIsLoading(true);
        setFullMenuData(null);
        setErrorMessage(null);

        try {
            const nextFullMenuData = await requestFullMenuData(foodMakerCode);
            if (serial !== requestSerial.current) return;
            setFullMenuData(nextFullMenuData);
            setLoadedFoodMaker(foodMakerCode);
        } catch (error) {
            if (serial !== requestSerial.current) return;
            setFullMenuData(null);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch the full menu data.');
        } finally {
            if (serial === requestSerial.current) setIsLoading(false);
        }
    }, [foodMakerCode]);

    useEffect(() => {
        if (!foodMakerCode || lastRequestedFoodMakerRef.current === foodMakerCode) {
            return;
        }
        /**
         * The restaurant code is the complete request identity. Recording it
         * before starting the request makes the effect idempotent when React
         * Strict Mode replays mount effects or session metadata changes without
         * changing the selected restaurant.
         */
        lastRequestedFoodMakerRef.current = foodMakerCode;
        loadFullMenuData();
    }, [foodMakerCode, loadFullMenuData]);

    if (!sessionData) {
        return <RouteLoadingState message="Loading admin page..." />;
    }

    if (isLoading || (loadedFoodMaker && loadedFoodMaker !== foodMakerCode)) {
        return <RouteLoadingState message="Loading admin menu data..." />;
    }

    if (!fullMenuData) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light px-3">
                <div className="w-100" style={{ maxWidth: '560px' }}>
                    <Alert variant="danger" className="mb-3">
                        {errorMessage ?? 'Failed to fetch the full menu data.'}
                    </Alert>
                    <div className="d-flex justify-content-center">
                        <Button variant="success" onClick={loadFullMenuData}>Retry</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <PartnerFullMenuDataContext.Provider value={fullMenuData}>
            <AdminMenuRefreshContext.Provider value={loadFullMenuData}>
                {children}
            </AdminMenuRefreshContext.Provider>
        </PartnerFullMenuDataContext.Provider>
    );
}

type AuthSessionResponse = {
    authenticated?: boolean;
    user?: AuthProfile | null;
    profile?: AuthProfile | null;
};

type AuthProfileResponse = AuthSessionResponse | AuthProfile | null;

function getAuthProfileFromResponse(payload: AuthProfileResponse): AuthProfile | null {
    if (!payload) {
        return null;
    }

    if ('authenticated' in payload || 'user' in payload || 'profile' in payload) {
        return payload.authenticated ? (payload.profile ?? payload.user ?? null) : null;
    }

    return payload as AuthProfile;
}

function isFilledProfileValue(value: unknown): boolean {
    return value !== null && value !== undefined && `${value}`.trim().length > 0;
}

function getFirstFilledProfileValue(...values: unknown[]): string | null {
    const value = values.find(isFilledProfileValue);
    return value === undefined ? null : `${value}`;
}

function getPriceLevelCacheKey(value: unknown): string {
    if (!isFilledProfileValue(value)) {
        return 'base';
    }

    const normalizedValue = `${value}`.trim();
    return normalizedValue === '0' ? 'base' : normalizedValue;
}

function applyTelegramIdentityToUserData(
    currentData: UserDataType | null,
    identity: TelegramMiniAppIdentity,
): UserDataType {
    const telegramUserId = resolvePreferredTelegramUserId(
        identity.userId,
        currentData?.telegram_user_id ?? currentData?.user_id ?? null,
    );
    const nextData: UserDataType = {
        ...(currentData ?? {
            lang: 'ru',
            channel: 'telegram',
            orders: [],
            update_time: Date.now(),
            loc_latitude: undefined,
            loc_longitude: undefined,
            location: undefined,
            countryCode: '',
            phone: '',
        }),
        user_id: telegramUserId,
        telegram_user_id: telegramUserId,
        channel: 'telegram',
        channel_first_name: identity.firstName ?? null,
        channel_last_name: identity.lastName ?? null,
        channel_user_name: identity.username ?? null,
        contacts: buildTelegramContacts(currentData?.contacts, identity.username),
        update_time: Date.now(),
    };

    if (!isFilledProfileValue(nextData.lang)) {
        nextData.lang = 'ru';
    }

    return nextData;
}

function mergeSessionDataWithAuthProfile(currentSessionData: SessionDataType | null, profile: AuthProfile): SessionDataType {
    const nextSessionData: SessionDataType = {
        ...(currentSessionData ?? {}),
    };
    const isTelegramProfile = profile.channel === 'telegram';
    const preferredTelegramUserId = isTelegramProfile
        ? resolvePreferredTelegramUserId(
            nextSessionData.telegram_user_id,
            nextSessionData.user_id,
            profile.user_id ?? null,
        )
        : null;

    if (isTelegramProfile) {
        if (preferredTelegramUserId) {
            nextSessionData.user_id = preferredTelegramUserId;
            nextSessionData.telegram_user_id = preferredTelegramUserId;
        }
        nextSessionData.channel = 'telegram';
        if (isFilledProfileValue(profile.channel_name)) nextSessionData.channel_name = profile.channel_name;
        if ('channel_first_name' in profile || 'display_name' in profile) nextSessionData.channel_first_name = profile.channel_first_name ?? profile.display_name ?? null;
        if ('channel_last_name' in profile) nextSessionData.channel_last_name = profile.channel_last_name ?? null;
        if ('channel_user_name' in profile || profile.contacts?.telegram) nextSessionData.channel_user_name = profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null;
        nextSessionData.contacts = {
            ...buildTelegramContacts(nextSessionData.contacts, profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null),
            ...(profile.contacts ?? {}),
            preferableMethod: 'telegram',
            telegram: {
                ...buildTelegramContacts(nextSessionData.contacts, profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null)?.telegram,
                ...(profile.contacts?.telegram ?? {}),
                userName: profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? undefined,
            },
        };
    } else {
        if (isFilledProfileValue(profile.user_id) && !isFilledProfileValue(nextSessionData.user_id)) nextSessionData.user_id = profile.user_id;
        if (isFilledProfileValue(profile.channel) && !isFilledProfileValue(nextSessionData.channel)) nextSessionData.channel = profile.channel;
        if (isFilledProfileValue(profile.channel_name) && !isFilledProfileValue(nextSessionData.channel_name)) nextSessionData.channel_name = profile.channel_name;
        if (isFilledProfileValue(profile.channel_first_name ?? profile.display_name) && !isFilledProfileValue(nextSessionData.channel_first_name)) {
            nextSessionData.channel_first_name = profile.channel_first_name ?? profile.display_name;
        }
        if (isFilledProfileValue(profile.channel_last_name) && !isFilledProfileValue(nextSessionData.channel_last_name)) nextSessionData.channel_last_name = profile.channel_last_name;
        if (isFilledProfileValue(profile.channel_user_name) && !isFilledProfileValue(nextSessionData.channel_user_name)) nextSessionData.channel_user_name = profile.channel_user_name;
        if (profile.contacts) {
            nextSessionData.contacts = {
                ...profile.contacts,
                ...nextSessionData.contacts,
                preferableMethod: nextSessionData.contacts?.preferableMethod ?? profile.contacts.preferableMethod,
                telegram: nextSessionData.contacts?.telegram ?? profile.contacts.telegram,
                phone: nextSessionData.contacts?.phone ?? profile.contacts.phone,
            };
        }
    }

    if (isFilledProfileValue(profile.lang) && !isFilledProfileValue(nextSessionData.lang)) nextSessionData.lang = profile.lang;
    if (isFilledProfileValue(profile.price_level) && !isFilledProfileValue(nextSessionData.price_level)) nextSessionData.price_level = profile.price_level;
    if (profile.loc_latitude !== null && profile.loc_latitude !== undefined && nextSessionData.loc_latitude == null) nextSessionData.loc_latitude = profile.loc_latitude;
    if (profile.loc_longitude !== null && profile.loc_longitude !== undefined && nextSessionData.loc_longitude == null) nextSessionData.loc_longitude = profile.loc_longitude;
    if (isFilledProfileValue(profile.location) && !isFilledProfileValue(nextSessionData.location)) nextSessionData.location = profile.location as string;
    if (isFilledProfileValue(profile.countryCode) && !isFilledProfileValue(nextSessionData.countryCode)) nextSessionData.countryCode = profile.countryCode as string;
    if (isFilledProfileValue(profile.phone) && !isFilledProfileValue(nextSessionData.phone)) nextSessionData.phone = profile.phone as string;
    nextSessionData.update_time = Date.now();

    applyDefaultLanguageToOrderData(nextSessionData);
    return nextSessionData;
}

function mergeUserDataWithAuthProfile(currentUserData: UserDataType | null, profile: AuthProfile): UserDataType {
    const nextUserData: UserDataType = {
        ...(currentUserData ?? {
            lang: undefined,
            channel: 'web',
            orders: [],
            update_time: Date.now(),
            loc_latitude: undefined,
            loc_longitude: undefined,
            location: undefined,
            countryCode: '',
            phone: '',
        }),
    };
    const isTelegramProfile = profile.channel === 'telegram';
    const preferredTelegramUserId = isTelegramProfile
        ? resolvePreferredTelegramUserId(
            nextUserData.telegram_user_id,
            nextUserData.user_id,
            profile.user_id ?? null,
        )
        : null;

    if (isTelegramProfile) {
        if (preferredTelegramUserId) {
            nextUserData.user_id = preferredTelegramUserId;
            nextUserData.telegram_user_id = preferredTelegramUserId;
        }
        nextUserData.channel = 'telegram';
        if (isFilledProfileValue(profile.channel_name)) nextUserData.channel_name = profile.channel_name;
        if ('channel_first_name' in profile || 'display_name' in profile) nextUserData.channel_first_name = profile.channel_first_name ?? profile.display_name ?? null;
        if ('channel_last_name' in profile) nextUserData.channel_last_name = profile.channel_last_name ?? null;
        if ('channel_user_name' in profile || profile.contacts?.telegram) nextUserData.channel_user_name = profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null;
        nextUserData.contacts = {
            ...buildTelegramContacts(nextUserData.contacts, profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null),
            ...(profile.contacts ?? {}),
            preferableMethod: 'telegram',
            telegram: {
                ...buildTelegramContacts(nextUserData.contacts, profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null)?.telegram,
                ...(profile.contacts?.telegram ?? {}),
                userName: profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? undefined,
            },
        };
    }

    if (isFilledProfileValue(profile.lang)) nextUserData.lang = profile.lang;
    if (isFilledProfileValue(profile.price_level)) nextUserData.price_level = profile.price_level;
    if (!isTelegramProfile && isFilledProfileValue(profile.user_id)) nextUserData.user_id = profile.user_id;
    if (isFilledProfileValue(profile.channel)) nextUserData.channel = profile.channel;
    if (isFilledProfileValue(profile.channel_name)) nextUserData.channel_name = profile.channel_name;
    if (isFilledProfileValue(profile.channel_first_name)) nextUserData.channel_first_name = profile.channel_first_name;
    if (isFilledProfileValue(profile.channel_last_name)) nextUserData.channel_last_name = profile.channel_last_name;
    if (isFilledProfileValue(profile.channel_user_name)) nextUserData.channel_user_name = profile.channel_user_name;
    if (profile.loc_latitude !== null && profile.loc_latitude !== undefined) nextUserData.loc_latitude = profile.loc_latitude;
    if (profile.loc_longitude !== null && profile.loc_longitude !== undefined) nextUserData.loc_longitude = profile.loc_longitude;
    if (isFilledProfileValue(profile.location)) nextUserData.location = profile.location;
    if (isFilledProfileValue(profile.countryCode)) nextUserData.countryCode = profile.countryCode as string;
    if (isFilledProfileValue(profile.phone)) nextUserData.phone = profile.phone as string;
    if (profile.contacts && !isTelegramProfile) nextUserData.contacts = profile.contacts;
    if (Array.isArray(profile.orders)) nextUserData.orders = profile.orders;
    nextUserData.update_time = Date.now();

    localStorage.setItem('userData', JSON.stringify(nextUserData));
    return nextUserData;
}

/**
 * App
 *
 * Owns application routing, shared menu/session/authentication state, and the
 * common backend requests passed to page components. It depends on browser
 * storage, URL parameters, Telegram Mini App identity data, and backend
 * bootstrap responses. Its effects initialize the session, load shared data,
 * synchronize authentication, and update route-level metadata.
 */
function App() {

    const [sessionData, setSessionData] = useState<SessionDataType | null>(null);           // The order data 
    const [userData, setUserData] = useState<UserDataType | null>(null);                    // The user data 
    const [partnerData, setPartnerData] = useState<PartnerDataType | null>(null);           // The partner data 
    const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
    const [authUser, setAuthUser] = useState<AuthProfile | null>(null);
    const [authError, setAuthError] = useState<string | null>(null);
    const [isProfileSaving, setIsProfileSaving] = useState<boolean>(false);
    const [homePlaces, setHomePlaces] = useState<HomePlace[]>([]);
    const [homePlacesStatus, setHomePlacesStatus] = useState<HomePlacesStatus>('idle');
    const [homePlacesError, setHomePlacesError] = useState<string | null>(null);
    const [homePlacesRequest, setHomePlacesRequest] = useState<HomePlacesRequest | null>(null);
    const [homePlacesReloadKey, setHomePlacesReloadKey] = useState<number>(0);
    const [activePathname, setActivePathname] = useState<string>(window.location.pathname);
    const selectAdminRestaurant = useCallback((foodMaker: string) => {
        setSessionData((current) => current && current.food_maker !== foodMaker
            ? { ...current, food_maker: foodMaker } : current);
        setPartnerData(null);
    }, []);

    const [foodMakersStatusData, setFoodMakersStatusData] = useState<FoodMakersStatusDataState>({
        records: [],
        status: 'idle',
        errorMessage: null,
        language: null,
    });
    const [isInitialMenuRequestFinished, setIsInitialMenuRequestFinished] = useState<boolean>(false);
    const [isSessionBootstrapResolved, setIsSessionBootstrapResolved] = useState<boolean>(false);

    const [showError, setShowError] = useState(false);              
    const [errorMessage, setErrorMessage] = useState('');
    // Parent render runs before Menu's effects, so these refs preserve the
    // original browser draft before the child can persist its default state.
    const initialSessionDataRef = useRef<SessionDataType>(readSessionDataStorage());
    const initialUserDataRef = useRef<UserDataType>(readUserDataStorage());
    const lastMenuFetchRef = useRef<{ foodMaker: string; lang: string; userId: string; priceLevel: string } | null>(null);
    const hasStartedAuthBootstrapRef = useRef<boolean>(false);
    const hasPromotedTelegramIdentityRef = useRef<boolean>(false);
    const foodMakersStatusRequestRef = useRef<{
        requestId: number;
        language: string | null;
        status: FoodMakersStatusDataState['status'];
    }>({ requestId: 0, language: null, status: 'idle' });
    const clearAdminCaches = useCallback(() => {
        foodMakersStatusRequestRef.current = { requestId: foodMakersStatusRequestRef.current.requestId + 1, language: null, status: 'idle' };
        setFoodMakersStatusData({ records: [], status: 'idle', errorMessage: null, language: null });
    }, []);


    const selectedHomeLocation = sessionData?.location ?? userData?.location ?? DEFAULT_HOME_LOCATION;

    const promoteTelegramIdentity = useCallback((identity: TelegramMiniAppIdentity | null) => {
        if (!identity?.userId) {
            return;
        }

        hasPromotedTelegramIdentityRef.current = true;
        setSessionData((currentData) => applyTelegramIdentityToSessionData(currentData, identity));
        setUserData((currentData) => applyTelegramIdentityToUserData(currentData, identity));
    }, []);

    useEffect(() => {
        if (!sessionData) {
            return;
        }

        localStorage.setItem('orderData', JSON.stringify(sessionData));
    }, [sessionData]);

    useEffect(() => {
        if (!userData) {
            return;
        }

        localStorage.setItem('userData', JSON.stringify(userData));
    }, [userData]);

    // Fetch the user data and session data from the url parameters and local storage  
    useEffect(() => {
        let isActive = true;

        const bootstrapUserData = async (): Promise<SessionBootstrapResult | null> => {
            const frontendBootstrap = await resolveFrontendBootstrap();

            if (frontendBootstrap?.matched) {
                const canonicalPath = frontendBootstrap.canonical_path?.trim().length
                    ? frontendBootstrap.canonical_path.trim()
                    : '/Menu';
                const canonicalPathname = normalizeCanonicalTarget(canonicalPath).pathname;
                const canonicalMenuUrl = buildCanonicalMenuUrl(canonicalPath);

                if (!ADMIN_PATHS.has(window.location.pathname) && window.location.pathname !== canonicalPathname) {
                    window.location.replace(canonicalMenuUrl);
                    return null;
                }

                if (!ADMIN_PATHS.has(window.location.pathname) && new URLSearchParams(window.location.search).has('fm')) {
                    window.history.replaceState(null, '', canonicalMenuUrl);
                }
            }

            return getUserData(
                frontendBootstrap,
                initialSessionDataRef.current,
                initialUserDataRef.current,
            );
        };

        const pendingBootstrap = sessionBootstrapCoordinator.run(bootstrapUserData);

        void pendingBootstrap
            .then((bootstrapResult) => {
                if (!isActive || !bootstrapResult) {
                    return;
                }

                setSessionData(bootstrapResult.sessionData);
                setUserData(bootstrapResult.userData);
                setIsSessionBootstrapResolved(true);
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Unknown error';
                console.error('Failed to initialize session data:', error);
                setShowError(true);
                setErrorMessage(`Failed to initialize session data: ${message}`);
            });

        return () => {
            isActive = false;
        };
    }, [])

    useEffect(() => {
        if (!isInitialMenuRequestFinished || hasStartedAuthBootstrapRef.current) {
            return;
        }

        hasStartedAuthBootstrapRef.current = true;
        const urlParams = new URLSearchParams(window.location.search.replace(/amp;/g, ''));
        const magicToken = urlParams.get('auth_token') ?? urlParams.get('magic_token');

        if (magicToken) {
            completeMagicLink(magicToken);
            urlParams.delete('auth_token');
            urlParams.delete('magic_token');
            const correctedUrl = new URL(window.location.href);
            correctedUrl.search = urlParams.toString();
            window.history.replaceState(null, '', correctedUrl.toString());
            return;
        }

        const bootstrapAuth = async () => {
            // An existing account or pending link has priority over automatic Mini App sign-in.
            const existing = await identityRequest<AuthSessionResponse>(backendUrl, '/auth/session').catch(() => null);
            const existingProfile = getAuthProfileFromResponse(existing);
            if (existingProfile) { applyAuthenticatedProfile(existingProfile); return; }
            if (sessionStorage.getItem('ifoodPendingAuthOperation')) { await refreshAuthSession(); return; }
            const telegramMiniAppIdentity = await waitForTelegramMiniAppIdentity('initData');

            if (telegramMiniAppIdentity?.initData) {
                loginWithTelegramMiniApp(telegramMiniAppIdentity.initData);
                return;
            }

            refreshAuthSession();
        };

        void bootstrapAuth();
    // Auth bootstrap must run once and stay independent from menu/session bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialMenuRequestFinished])

    useEffect(() => {
        if (!isSessionBootstrapResolved || hasPromotedTelegramIdentityRef.current || authStatus !== 'anonymous'
            || sessionStorage.getItem('ifoodPendingAuthOperation')) {
            return;
        }

        let isCancelled = false;

        const syncTelegramIdentity = async () => {
            const telegramMiniAppIdentity = await waitForTelegramMiniAppIdentity('userId');
            if (isCancelled || !telegramMiniAppIdentity?.userId) {
                return;
            }

            promoteTelegramIdentity(telegramMiniAppIdentity);
        };

        void syncTelegramIdentity();

        return () => {
            isCancelled = true;
        };
    }, [isSessionBootstrapResolved, promoteTelegramIdentity, authStatus]);

    // Fetch the partner data needed by the public menu route. The editable full
    // menu payload is loaded only inside admin route boundaries.
    useEffect(() => {
        if (!isSessionBootstrapResolved) {
            return;
        }
        
        if (sessionData && sessionData.food_maker) {
            const foodMakerCode = sessionData.food_maker;
            const lang = sessionData.lang ?? 'en';
            const userId = getFirstFilledProfileValue(sessionData.user_id, userData?.user_id);
            const userIdCacheKey = userId ?? '';
            const priceLevelCacheKey = getPriceLevelCacheKey(sessionData.price_level ?? userData?.price_level);
            if (
                lastMenuFetchRef.current
                && lastMenuFetchRef.current.foodMaker === foodMakerCode
                && lastMenuFetchRef.current.lang === lang
                && lastMenuFetchRef.current.userId === userIdCacheKey
                && lastMenuFetchRef.current.priceLevel === priceLevelCacheKey
            ) {
                return;
            }

            lastMenuFetchRef.current = { foodMaker: foodMakerCode, lang, userId: userIdCacheKey, priceLevel: priceLevelCacheKey };
            fetchPartnerData(foodMakerCode, lang, userId).finally(() => {
                setIsInitialMenuRequestFinished(true);
            });
        } else {
            console.log('No food maker code found in session data. Cannot fetch the menu and partner data.');
            setIsInitialMenuRequestFinished(true);
        };

    }, [
        isSessionBootstrapResolved,
        sessionData?.food_maker,
        sessionData?.lang,
        sessionData?.price_level,
        sessionData?.user_id,
        userData?.price_level,
        userData?.user_id,
    ])

    useEffect(() => {
        // Home discovery data is shared only by the Home and Search routes.
        // Keeping this effect route-scoped prevents unrelated administration
        // pages from issuing a second, location-filtered `/food_makers` request.
        if (!isSessionBootstrapResolved || !HOME_PLACES_PATHS.has(activePathname)) {
            return;
        }

        const nextRequest: HomePlacesRequest = {
            language: sessionData?.lang ?? userData?.lang ?? 'en',
            userId: sessionData?.user_id ?? userData?.user_id ?? null,
            location: selectedHomeLocation,
        };
        let isRequestActive = true;

        setHomePlacesStatus('loading');
        setHomePlacesError(null);
        setHomePlacesRequest(nextRequest);

        requestHomePlaces(nextRequest)
            .then((places) => {
                if (!isRequestActive) {
                    return;
                }

                setHomePlaces(places);
                setHomePlacesStatus('success');
            })
            .catch((error) => {
                if (!isRequestActive) {
                    return;
                }

                setHomePlaces([]);
                setHomePlacesStatus('error');
                setHomePlacesError(error instanceof Error ? error.message : 'Failed to fetch the home page places.');
            });

        return () => {
            isRequestActive = false;
        };
    }, [
        activePathname,
        homePlacesReloadKey,
        isSessionBootstrapResolved,
        selectedHomeLocation,
        sessionData?.lang,
        sessionData?.user_id,
        userData?.lang,
        userData?.user_id,
    ])

    const reloadHomePlaces = useCallback(() => {
        setHomePlacesReloadKey((currentValue) => currentValue + 1);
    }, []);

    const selectHomeLocation = useCallback((locationCode: string) => {
        const updateTime = Date.now();
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('loc', locationCode);
        window.history.replaceState(null, '', nextUrl.toString());

        const storedOrderData = readSessionDataStorage() as Partial<SessionDataType>;
        const nextOrderData: SessionDataType = {
            ...storedOrderData,
            location: locationCode,
            update_time: updateTime,
        };
        localStorage.setItem('orderData', JSON.stringify(nextOrderData));
        setSessionData((currentData) => ({
            ...(currentData ?? {}),
            location: locationCode,
            update_time: updateTime,
        }));

        const storedUserData = readUserDataStorage() as Partial<UserDataType>;
        localStorage.setItem('userData', JSON.stringify({
            ...storedUserData,
            location: locationCode,
            update_time: updateTime,
        }));
        setUserData((currentData) => (
            currentData
                ? {
                    ...currentData,
                    location: locationCode,
                    update_time: updateTime,
                }
                : currentData
        ));
    }, []);

    /**
     * Owns loading, caching, retry, and stale-response protection for the global
     * status dataset. The stable callback lets the lazy page request its active
     * language without creating duplicate requests during React Strict Mode.
     */
    const loadFoodMakersStatus = useCallback(async (language: string, force = false): Promise<void> => {
        const normalizedLanguage = language.trim().length > 0 ? language : 'en';
        const currentRequest = foodMakersStatusRequestRef.current;
        if (
            !force
            && currentRequest.language === normalizedLanguage
            && (currentRequest.status === 'loading' || currentRequest.status === 'success')
        ) {
            return;
        }

        const requestId = currentRequest.requestId + 1;
        foodMakersStatusRequestRef.current = {
            requestId,
            language: normalizedLanguage,
            status: 'loading',
        };
        setFoodMakersStatusData((currentData) => ({
            records: currentData.language === normalizedLanguage ? currentData.records : [],
            status: 'loading',
            errorMessage: null,
            language: normalizedLanguage,
        }));

        try {
            const records = await requestFoodMakersStatus(normalizedLanguage);
            if (foodMakersStatusRequestRef.current.requestId !== requestId) {
                return;
            }

            foodMakersStatusRequestRef.current.status = 'success';
            setFoodMakersStatusData({
                records,
                status: 'success',
                errorMessage: null,
                language: normalizedLanguage,
            });
        } catch (error) {
            if (foodMakersStatusRequestRef.current.requestId !== requestId) {
                return;
            }

            foodMakersStatusRequestRef.current.status = 'error';
            setFoodMakersStatusData((currentData) => ({
                records: currentData.language === normalizedLanguage ? currentData.records : [],
                status: 'error',
                errorMessage: error instanceof Error ? error.message : 'Failed to fetch food makers.',
                language: normalizedLanguage,
            }));
        }
    }, []);

    async function getUserData(
        frontendBootstrap: FrontendBootstrapResponse | null = null,
        storedSessionData: SessionDataType = readSessionDataStorage(),
        storedUserData: UserDataType = readUserDataStorage(),
    ): Promise<SessionBootstrapResult> {
        // Get the orderData and userData parameters from the local storage. It uses the temporary variables to avoid 
        // changing useState variables and to prevent unnecessary re-renders in this way
        const orderDataTemp: SessionDataType = { ...storedSessionData };
        const userDataTemp: UserDataType = { ...storedUserData };

        // Create the orders property (contains the active orders information: id, receipt and status) if it doesn't exist
        if (!('orders' in userDataTemp) || !Array.isArray(userDataTemp.orders)) userDataTemp.orders = [];

        // Remove 'amp;' (telegram adds it for something) from the url if this String exists
        const urlParameters = window.location.search.replace(/amp;/g, '');

        // Get the parameters from the URL and update the constants and variables
        const urlParams = new URLSearchParams(urlParameters);

        const bootstrapFoodMaker = frontendBootstrap?.matched
            ? normalizeFoodMakerCode(frontendBootstrap.food_maker ?? null)
            : null;
        let foodMaker = bootstrapFoodMaker ?? normalizeFoodMakerCode(urlParams.get('fm') || null);                    // Get the foodMaker code e.g. 'Kraken_Roll'

        let userID = urlParams.get('ui') || null;                       // Get the user id
        let channel = urlParams.get('ch') || null;                      // Get the channel (usually is telegram)
        let channelName = urlParams.get('chn') || null;                 // Get the channel (usually is telegram bot name)
        let lang = urlParams.get('lang') || null;                       // Get the language
        let location = urlParams.get('loc') || null;                    // Get the location
        let ads = urlParams.get('ads') || null;                         // Advertisement code. It helps to identify the exact ad

        let channel_first_name = null;
        let channel_last_name = null;
        let channel_user_name = null;

        // Remove a space and a '+' symbols from the userID in case if they are
        if (userID) {
            userID = userID.replace(" ", "")
            userID = userID.replace("+", "")
        }    

        const telegramMiniAppIdentity = await waitForTelegramMiniAppIdentity('userId');

        // Telegram Mini App identity must override URL and stale local storage data when it is available at runtime.
        if (telegramMiniAppIdentity?.userId) {
            userID = telegramMiniAppIdentity.userId;
            channel = 'telegram';
            channel_first_name = telegramMiniAppIdentity.firstName;
            channel_last_name = telegramMiniAppIdentity.lastName;
            channel_user_name = telegramMiniAppIdentity.username;

        // Check if we have the user information in the local storage
        } else if (isFilledProfileValue(userDataTemp.user_id) && !isFilledProfileValue(orderDataTemp.user_id)) {
            userID = userDataTemp.user_id ?? null;              
            channel = userDataTemp.channel ?? null;
            if ('channel_name' in userDataTemp) {channelName = userDataTemp.channel_name ?? null};
            if ('lang' in userDataTemp) {lang = userDataTemp.lang ?? null};
            if ('location' in userDataTemp) {location = userDataTemp.location ?? null};
        };
        if (!('price_level' in orderDataTemp) && 'price_level' in userDataTemp) {
            orderDataTemp.price_level = userDataTemp.price_level;
        }

        // Wholesale Bottle Shop follows the legacy Chinese default when no
        // explicit URL language or user-data fallback has already selected one.
        if (lang === null && isWholesaleBottleShopHost(currentHostname)) {
            lang = 'cn';
        }

        // Update the orderData parameters
        if (foodMaker !== null) {orderDataTemp['food_maker'] = foodMaker};
        if (userID !== null) {orderDataTemp['user_id'] = userID};
        if (channelName !== null) {orderDataTemp['channel_name'] = channelName};
        if (lang !== null) {orderDataTemp['lang'] = lang};
        if (location !== null) {orderDataTemp['location'] = location};
        if (ads !== null) {orderDataTemp['ads'] = ads};
        if (channel !== null) {orderDataTemp['channel'] = channel}
        if (channel_first_name !== null) {orderDataTemp['channel_first_name'] = channel_first_name}
        if (channel_last_name !== null) {orderDataTemp['channel_last_name'] = channel_last_name}
        if (channel_user_name !== null) {orderDataTemp['channel_user_name'] = channel_user_name}
        else if (!('channel' in orderDataTemp)) {orderDataTemp['channel'] = 'web'};

        if (telegramMiniAppIdentity?.userId) {
            Object.assign(orderDataTemp, applyTelegramIdentityToSessionData(orderDataTemp, telegramMiniAppIdentity));
        }

        normalizeTelegramChannelWithoutUserId(orderDataTemp);
        applyDefaultLanguageToOrderData(orderDataTemp);
        orderDataTemp['update_time'] = Date.now();
        localStorage.setItem('orderData', JSON.stringify(orderDataTemp));
        const initialUserData = updateUserData();

        const shouldFetchUserInfo = isFilledProfileValue(orderDataTemp.user_id) && !(
            'loc_latitude' in orderDataTemp
            && 'loc_longitude' in orderDataTemp
            && lang !== null
            && location !== null
            && Date.now() - (orderDataTemp.update_time ?? 0) < 300000
        );

        // Get the information about the user from the back if one of the parameters is empty or the last update was more then 5 min ago
        if (shouldFetchUserInfo) {
            try {
                const response = await fetch(backendUrl + '/getUserInfo?user_id=' + orderDataTemp.user_id, {
                    method: 'GET', headers: {'ngrok-skip-browser-warning': 'true'}});

                if (!response.ok) {
                    throw new Error(`User information request failed with status ${response.status}`);
                }

                const payload = await response.json();

                orderDataTemp['loc_latitude'] = payload.loc_latitude;
                orderDataTemp['loc_longitude'] = payload.loc_longitude;
                if ('price_level' in payload) orderDataTemp['price_level'] = payload.price_level;
                if (channel_user_name == null) orderDataTemp['channel_user_name'] = payload.channel_user_name;

                // Set the language if we don't have an information about it yet
                if (!('lang' in orderDataTemp) || orderDataTemp.lang == null || orderDataTemp.lang == 'undefined')
                    {orderDataTemp['lang'] = payload.language};

                // Set the location if we don't have an information about it yet
                if (!('location' in orderDataTemp) || orderDataTemp.location == null || orderDataTemp.location == 'undefined')
                    {orderDataTemp['location'] = payload.location};

                userDataTemp['orders'] = payload.orders;      // Add the active orders id and status

                applyDefaultLanguageToOrderData(orderDataTemp);
                orderDataTemp['update_time'] = Date.now();
                localStorage.setItem('orderData', JSON.stringify(orderDataTemp));

                return {
                    sessionData: { ...orderDataTemp },
                    userData: updateUserData(),
                };
            } catch (error) {
                console.error('Error fetching user information:', error);
            }

            return {
                sessionData: { ...orderDataTemp },
                userData: initialUserData,
            };
        };

            function updateUserData(): UserDataType {
                if ('user_id' in orderDataTemp) {
                    if (isFilledProfileValue(orderDataTemp.user_id)) {
                        userDataTemp['user_id'] = orderDataTemp.user_id;
                    } else {
                        delete userDataTemp.user_id;
                    }
                };
                if ('telegram_user_id' in orderDataTemp) {
                    if (isFilledProfileValue(orderDataTemp.telegram_user_id)) {
                        userDataTemp['telegram_user_id'] = orderDataTemp.telegram_user_id;
                    } else {
                        delete userDataTemp.telegram_user_id;
                    }
                };
                if ('price_level' in orderDataTemp) {userDataTemp['price_level'] = orderDataTemp.price_level};
                if ('lang' in orderDataTemp) {userDataTemp['lang'] = orderDataTemp.lang};
                if ('loc_latitude' in orderDataTemp) {userDataTemp['loc_latitude'] = orderDataTemp.loc_latitude};
                if ('loc_longitude' in orderDataTemp) {userDataTemp['loc_longitude'] = orderDataTemp.loc_longitude};
                if ('location' in orderDataTemp) {userDataTemp['location'] = orderDataTemp.location};
                if ('channel' in orderDataTemp) {userDataTemp['channel'] = orderDataTemp.channel};
                if ('channel_name' in orderDataTemp) {userDataTemp['channel_name'] = orderDataTemp.channel_name};
                if ('channel_first_name' in orderDataTemp) {userDataTemp['channel_first_name'] = orderDataTemp.channel_first_name};
                if ('channel_last_name' in orderDataTemp) {userDataTemp['channel_last_name'] = orderDataTemp.channel_last_name};
                if ('channel_user_name' in orderDataTemp) {userDataTemp['channel_user_name'] = orderDataTemp.channel_user_name};
                if ('contacts' in orderDataTemp) {userDataTemp['contacts'] = orderDataTemp.contacts};

                if (telegramMiniAppIdentity?.userId) {
                    Object.assign(userDataTemp, applyTelegramIdentityToUserData(userDataTemp, telegramMiniAppIdentity));
                }

                userDataTemp['update_time'] = Date.now();
                localStorage.setItem('userData', JSON.stringify(userDataTemp));

                return { ...userDataTemp };
            };

            return {
                sessionData: { ...orderDataTemp },
                userData: initialUserData,
            };

        };

    // Fetch the partner and client menu data 
    async function fetchPartnerData(foodMakerCode: string, lang: string, userId: string | null): Promise<PartnerDataType | null> {
        const apiUrl: string = buildPublicMenuUrl(foodMakerCode, lang, userId);

        try {
            const response = await fetch(apiUrl, {method: 'GET', headers: {'ngrok-skip-browser-warning': 'true'}});
            const freshPartnerData = await response.json() as PartnerDataType;
            setPartnerData(freshPartnerData);
            console.log('PartnerData', freshPartnerData);
            return freshPartnerData;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            console.log('Failed to fetch the partner data', message);
            setShowError(true);                                         // Show the error message
            setErrorMessage('Failed to fetch the partner data: ' + message);
            return null;
        }
    };

    function applyAuthenticatedProfile(profile: AuthProfile | null) {
        if (!profile) {
            setAuthStatus('anonymous');
            setAuthUser(null);
            return;
        }

        setAuthStatus('authenticated');
        setAuthUser(profile);
        setAuthError(null);
        setSessionData((currentSessionData) => mergeSessionDataWithAuthProfile(currentSessionData, profile));
        setUserData((currentUserData) => mergeUserDataWithAuthProfile(currentUserData, profile));
    }

    async function postAuthJson(endpoint: string, body: Record<string, unknown>): Promise<AuthProfile | null> {
        const payload = await identityRequest<AuthProfileResponse>(backendUrl, endpoint, 'POST', body);
        return getAuthProfileFromResponse(payload);
    }

    async function refreshAuthSession() {
        try {
            const response = await fetch(`${backendUrl}/auth/session`, {
                method: 'GET',
                credentials: 'include',
                headers: {'ngrok-skip-browser-warning': 'true'},
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const payload = await response.json() as AuthSessionResponse;
            applyAuthenticatedProfile(getAuthProfileFromResponse(payload));
        } catch (error) {
            console.warn('Authentication session bootstrap failed. Continuing anonymously.', error);
            setAuthStatus('anonymous');
            setAuthUser(null);
        }
    }

    async function startIdentityFlow(provider: string, purpose: AuthPurpose, email?: string): Promise<AuthFlowStart> {
        return identityRequest<AuthFlowStart>(backendUrl, purpose === 'link' ? '/auth/identities/link/start' : '/auth/flows/start', 'POST', {
            provider, purpose, email, return_url: authReturnUrl(),
        });
    }

    async function requestEmailMagicLink(email: string, purpose: AuthPurpose = 'login') {
        setAuthError(null);

        try {
            const response = await fetch(`${backendUrl}/auth/email/start`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true',
                },
                body: JSON.stringify({email, purpose, return_url: authReturnUrl()}),
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }
        } catch (error) {
            if (!authUser) setAuthStatus('error');
            setAuthError(error instanceof Error ? error.message : 'Failed to send the login link.');
            throw error;
        }
    }

    async function completeMagicLink(token: string) {
        setAuthStatus('loading');
        setAuthError(null);

        try {
            const profile = await postAuthJson('/auth/email/complete', {token});
            applyAuthenticatedProfile(profile);
        } catch (error) {
            await refreshAuthSession();
            setAuthError(error instanceof Error ? error.message : 'Failed to complete email login.');
        }
    }

    function startGoogleLogin(purpose: AuthPurpose = 'login') {
        const returnUrl = encodeURIComponent(authReturnUrl());
        window.location.href = `${backendUrl}/auth/google/start?purpose=${purpose}&return_url=${returnUrl}`;
    }

    async function loginWithTelegramWidget(telegramPayload: Record<string, unknown>, purpose: AuthPurpose = 'login') {
        setAuthStatus('loading');
        setAuthError(null);

        try {
            const flow = await startIdentityFlow('telegram', purpose);
            const profile = await postAuthJson('/auth/telegram/widget', { ...telegramPayload, flow_token: flow.flow_token });
            applyAuthenticatedProfile(profile);
        } catch (error) {
            await refreshAuthSession();
            setAuthError(error instanceof Error ? error.message : 'Failed to login with Telegram.');
        }
    }

    async function loginWithTelegramMiniApp(initData: string, purpose: AuthPurpose = 'login') {
        setAuthStatus('loading');
        setAuthError(null);

        try {
            const flow = await startIdentityFlow('telegram', purpose);
            const profile = await postAuthJson('/auth/telegram/miniapp', {init_data: initData, flow_token: flow.flow_token});
            // The verified Human/profile is authoritative; unsigned launch data is only an anonymous draft.
            applyAuthenticatedProfile(profile);
        } catch (error) {
            console.warn('Telegram Mini App authentication failed. Continuing with the legacy session data.', error);
            await refreshAuthSession();
        }
    }

    async function saveProfile(profile: AuthProfile) {
        setIsProfileSaving(true);
        setAuthError(null);

        try {
            const payload = await identityRequest<AuthProfileResponse>(backendUrl, '/auth/profile', 'PATCH', profile);
            const savedProfile = getAuthProfileFromResponse(payload);
            applyAuthenticatedProfile(savedProfile ?? profile);
        } catch (error) {
            setAuthError(error instanceof Error ? error.message : 'Failed to save the profile.');
            throw error;
        } finally {
            setIsProfileSaving(false);
        }
    }

    async function logout() {
        try {
            await fetch(`${backendUrl}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {'ngrok-skip-browser-warning': 'true'},
            });
        } catch (error) {
            console.warn('Failed to notify backend about logout. Clearing frontend auth state.', error);
        } finally {
            setAuthStatus('anonymous');
            setAuthUser(null);
            setAuthError(null);
        }
    }

    const refreshPartnerData = async (): Promise<PartnerDataType | null> => {
        if (!sessionData?.food_maker) {
            return null;
        }

        const lang = sessionData.lang ?? 'en';
        const userId = getFirstFilledProfileValue(sessionData.user_id, userData?.user_id);
        return fetchPartnerData(sessionData.food_maker, lang, userId);
    };

    const authContextValue: AuthContextType = {
        authStatus,
        authUser,
        authError,
        isProfileSaving,
        requestEmailMagicLink,
        completeMagicLink,
        startGoogleLogin,
        loginWithTelegramWidget,
        loginWithTelegramMiniApp,
        saveProfile,
        logout,
        refreshAuthSession,
        identityActions: {
            list: () => identityRequest<IdentitySettings>(backendUrl, '/auth/identities'),
            start: startIdentityFlow,
            disconnect: async (id) => {
                await identityRequest(backendUrl, '/auth/identities/' + id, 'DELETE');
                await refreshAuthSession();
            },
            completeTelegram: async (flowToken, evidence) => {
                const endpoint = evidence.init_data ? '/auth/telegram/miniapp' : '/auth/telegram/widget';
                const profile = await postAuthJson(endpoint, { ...evidence, flow_token: flowToken });
                applyAuthenticatedProfile(profile);
            },
        },
    };

    const homeDataContextValue = useMemo<HomeDataContextType>(() => ({
        places: homePlaces,
        status: homePlacesStatus,
        errorMessage: homePlacesError,
        request: homePlacesRequest,
        selectedLocation: selectedHomeLocation,
        sessionData,
        userData,
        reloadPlaces: reloadHomePlaces,
        selectLocation: selectHomeLocation,
    }), [
        homePlaces,
        homePlacesError,
        homePlacesRequest,
        homePlacesStatus,
        reloadHomePlaces,
        selectHomeLocation,
        selectedHomeLocation,
        sessionData,
        userData,
    ]);


  return (
    <BrowserRouter basename="/"> {/* If your React app is under /admin */}
      <AppRoutePathObserver onPathnameChange={setActivePathname} />
      <AuthContext.Provider value={authContextValue}>
          <SessionDataContext.Provider value={sessionData}>
            <UserDataContext.Provider value={userData}>
              <HomeDataContext.Provider value={homeDataContextValue}>
                <PartnerDataContext.Provider value={partnerData}>
                  <PartnerDataRefreshContext.Provider value={refreshPartnerData}>
                    <AdminAccessProvider authStatus={authStatus} authUser={authUser} refreshAuthSession={refreshAuthSession} clearAdminCaches={clearAdminCaches}>
                    <DevelopmentRenderErrorTrigger />
                    <BrowserTabBranding
                      partnerData={partnerData}
                      frontendOrigin={currentOrigin}
                      frontendHostname={currentHostname}
                      logoBaseUrl={logoImagesPath}
                    />
                    <TestBackendNotificationBar />
                    <AppVersionInfoModal backendUrl={backendUrl} />
                    <Routes>
                      <Route
                        path="/Home"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading home page..." />}>
                            <Home />
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/Search"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading search page..." />}>
                            <Search />
                          </Suspense>
                        )}
                      />
                      <Route path="/Menu" element={<Menu />} />
                      <Route path="/menu" element={<Menu />} />
                      <Route path="/orders" element={<Orders fetchNotificationMessage={fetchNotificationMessage} />} />
                      <Route
                        path="/FoodMakersStatus"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading food makers status page..." />}>
                            <AdminRouteGuard global>
                              <FoodMakersStatus
                                dataState={foodMakersStatusData}
                                loadFoodMakers={loadFoodMakersStatus}
                              />
                            </AdminRouteGuard>
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/TelegramLinkConverter"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading Telegram link converter..." />}>
                            <AdminRouteGuard global><TelegramLinkConverter /></AdminRouteGuard>
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/"
                        element={<TemporaryStaticHomeRedirect />}
                      />
                      <Route
                        path="/MenuEditing"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading admin page..." />}>
                            <AdminRouteGuard onSelectRestaurant={selectAdminRestaurant}>
                              <AdminFullMenuBoundary>
                                <MenuEditing />
                              </AdminFullMenuBoundary>
                            </AdminRouteGuard>
                          </Suspense>
                        )}
                      />
                      <Route
                        path="/ProductEditing"
                        element={(
                          <Suspense fallback={<RouteLoadingState message="Loading product editor..." />}>
                            <AdminRouteGuard onSelectRestaurant={selectAdminRestaurant}>
                              <AdminFullMenuBoundary><ProductEditing /></AdminFullMenuBoundary>
                            </AdminRouteGuard>
                          </Suspense>
                        )}
                      />
                    </Routes>
                    </AdminAccessProvider>
                  </PartnerDataRefreshContext.Provider>
                </PartnerDataContext.Provider>
              </HomeDataContext.Provider>
            </UserDataContext.Provider>
          </SessionDataContext.Provider>
      </AuthContext.Provider>
    </BrowserRouter>
  );
}

export default App;
